import React from 'react'
import * as RadixAccordion from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '../icons/ChevronDownIcon'
import { lit, buildClassName } from 'src/utils'

export function Accordion({
    children,
    className,
    analyticsClassName = buildClassName('Accordion'),
}: {
    children: React.ReactNode

    /**
     * The classname -- this is mostly used to hook GA event tracking
     */
    className?: string

    /* The class name to be used to hook GA event tracking
     */
    analyticsClassName?: string
}) {
    return (
        <RadixAccordion.Root
            className={lit`w-full rounded-md ${className} ${analyticsClassName}`}
            type="single"
            collapsible
        >
            {React.Children.map(children, (child, i) => {
                if ((child as React.ReactElement)?.type !== AccordionItem) {
                    if (process.env.NODE_ENV === 'development') {
                        throw Error(
                            'All direct children of Accordion must be Accordion.Item components'
                        )
                    }
                    return null
                }
                const accordionItem =
                    child as React.ReactElement<AccordionItemProps>
                return React.cloneElement(accordionItem, {
                    value: (accordionItem.props as any).value ?? i.toString(),
                })
            })}
        </RadixAccordion.Root>
    )
}

type AccordionItemProps = Omit<RadixAccordion.AccordionItemProps, 'value'> & {
    // make `value` prop optional
    value?: string
}

const AccordionItem = React.forwardRef<
    React.ComponentRef<typeof RadixAccordion.Item>,
    AccordionItemProps
>(({ children, className, value, ...props }, forwardedRef) => (
    <RadixAccordion.Item
        {...props}
        value={value!} // `value` will always be set thanks to the cloneElement() in Accordion()
        ref={forwardedRef}
        className={lit`mt-px w-full overflow-hidden border-b-[1px] border-neutral-200 py-6 first:mt-0 first:rounded-t last:rounded-b last:border-none focus-within:relative ${className}`}
    >
        {children}
    </RadixAccordion.Item>
))

const AccordionTrigger = React.forwardRef<
    React.ComponentRef<typeof RadixAccordion.Trigger>,
    RadixAccordion.AccordionTriggerProps
>(({ children, className, ...props }, forwardedRef) => (
    <RadixAccordion.Header className="flex">
        <RadixAccordion.Trigger
            className={lit`text-violet11 group flex min-h-7 flex-1 cursor-pointer items-center justify-between pr-0 text-lg font-medium outline-none ${className}`}
            {...props}
            ref={forwardedRef}
        >
            {children}
            <ChevronDownIcon
                className={lit`text-violet10 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-180`}
                aria-hidden
            />
        </RadixAccordion.Trigger>
    </RadixAccordion.Header>
))

const AccordionContent = React.forwardRef<
    React.ComponentRef<typeof RadixAccordion.Content>,
    RadixAccordion.AccordionContentProps
>(({ children, className, ...props }, forwardedRef) => (
    <RadixAccordion.Content
        className={lit`data-[state=open]:animate-accordionSlideDown data-[state=closed]:animate-accordionSlideUp overflow-hidden text-base data-[state=open]:mt-6 ${className}`}
        {...props}
        ref={forwardedRef}
    >
        <div className="justify-start text-left">{children}</div>
    </RadixAccordion.Content>
))

AccordionItem.displayName = 'AccordionItem'
AccordionTrigger.displayName = 'AccordionTrigger'
AccordionContent.displayName = 'AccordionContent'
Accordion.Item = AccordionItem
Accordion.Trigger = AccordionTrigger
Accordion.Content = AccordionContent
